import { create } from 'zustand';
import api from '@/lib/api';

export const useBranchStore = create((set, get) => ({
  branches: [],
  loading: false,
  _lastFetch: null,   // ts of last successful load (freshness window)
  _inflight: null,    // dedupe concurrent callers

  // iter961 — Single source of truth for the "active branch" shared across
  // POS, Stock and Reports. Persisted under the legacy `pos_active_branch_id`
  // key so every existing reader (and localStorage lookups) stays compatible.
  activeBranchId: (() => {
    try { return localStorage.getItem('pos_active_branch_id') || null; } catch { return null; }
  })(),
  setActiveBranchId: (id) => {
    try {
      if (id) localStorage.setItem('pos_active_branch_id', id);
      else localStorage.removeItem('pos_active_branch_id');
    } catch { /* ignore */ }
    set({ activeBranchId: id });
  },

  fetchBranches: async (force = false) => {
    const st = get();
    // Dedupe concurrent callers (e.g. several pickers mount together).
    if (st._inflight) return st._inflight;
    // iter964 — serve cache only within a short freshness window; a page
    // opened later always refetches so newly-added/edited branches appear
    // (the old length-only guard cached the list for the whole session,
    // which made Sales/Expenses show a stale subset vs fresh-fetch pages).
    const fresh = st._lastFetch && (Date.now() - st._lastFetch < 15000);
    if (!force && fresh && st.branches.length > 0) return st.branches;
    const p = (async () => {
      try {
        const res = await api.get('/branches');
        set({ branches: res.data, loading: false, _lastFetch: Date.now(), _inflight: null });
        return res.data;
      } catch {
        set({ loading: false, _inflight: null });
        return get().branches;
      }
    })();
    set({ loading: true, _inflight: p });
    return p;
  },

  getBranchName: (branchId) => {
    const { branches } = get();
    if (!branchId) return 'All Branches';
    return branches.find(b => b.id === branchId)?.name || 'All Branches';
  },

  reset: () => set({ branches: [], loading: false, _lastFetch: null, _inflight: null }),
}));
